import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, ChevronLeft, ChevronRight, Rocket } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminData, SearchBox, FilterSelect, Pager, TableCard, TH } from "./shared";
import StatusBadge from "../../components/StatusBadge";
import { fmtDateTime, fmtMoney } from "../../lib/format";

const now = () => new Date();

// Server status is draft/active/disabled; Scheduled & Expired are derived from
// the canonical validity window stored on the promotion.
function classify(p) {
  const t = now();
  if (p.status === "draft") return "drafts";
  if (p.status === "disabled") return "expired";
  if (p.starts_at && new Date(p.starts_at) > t) return "scheduled";
  if (p.ends_at && new Date(p.ends_at) < t) return "expired";
  return "active";
}

const STEPS = ["Details", "Audience", "Discount", "Validity", "Usage rules", "Review", "Publish"];
const EMPTY = { code: "", audience: "drivers", discount_percent: "", starts_at: "", ends_at: "", max_redemptions: "", per_user_limit: "1" };

function CreateWizard({ onClose, onPublished, prefill }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(prefill || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const stepValid = () => {
    if (step === 0) return /^[A-Z0-9][A-Z0-9-]{2,31}$/.test(f.code.trim().toUpperCase());
    if (step === 1) return ["drivers", "hosts", "both"].includes(f.audience);
    if (step === 2) { const d = parseFloat(f.discount_percent); return !isNaN(d) && d > 0 && d <= 100; }
    if (step === 3) return !f.starts_at || !f.ends_at || new Date(f.ends_at) > new Date(f.starts_at);
    if (step === 4) return parseInt(f.per_user_limit, 10) >= 1 && (!f.max_redemptions || parseInt(f.max_redemptions, 10) >= 1);
    return true;
  };

  const payload = () => ({
    code: f.code.trim().toUpperCase(),
    discount_percent: parseFloat(f.discount_percent),
    audience: f.audience,
    starts_at: f.starts_at || null,
    ends_at: f.ends_at || null,
    max_redemptions: f.max_redemptions ? parseInt(f.max_redemptions, 10) : null,
    per_user_limit: parseInt(f.per_user_limit, 10) || 1,
  });

  const publish = async () => {
    setBusy(true);
    try {
      const created = await api.adminCreatePromotion(payload());
      await api.adminUpdatePromotion(created.promotion_id, { action: "activate" });
      toast.success(`Promotion ${created.code} is live`);
      onPublished();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="promo-wizard" role="dialog" aria-label="Create promotion">
      <div className="card-dark w-full max-w-lg p-7 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-semibold text-lg">Create promotion</h3>
          <span className="text-[11px] text-zinc-500">Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
        </div>

        {step === 0 && (
          <div>
            <label htmlFor="pz-code" className="label-dark">Promo code</label>
            <input id="pz-code" data-testid="pz-code" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())}
                   className="input-dark uppercase" placeholder="MRP-WEEKEND" maxLength={32} />
            <p className="mt-2 text-[11px] text-zinc-600">3–32 characters: A–Z, 0–9, dashes. Drivers/hosts type this at checkout.</p>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-2" data-testid="pz-audience">
            {[["drivers", "Drivers — discount on their parking fare"], ["hosts", "Hosts — only host accounts can redeem"], ["both", "Both — any account"]].map(([v, l]) => (
              <label key={v} className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${f.audience === v ? "border-[#FF3B45] bg-[#FF3B45]/5" : "border-[#27272A]"}`}>
                <input type="radio" name="pz-aud" checked={f.audience === v} onChange={() => set("audience", v)} data-testid={`pz-aud-${v}`} />
                <span className="text-sm">{l}</span>
              </label>
            ))}
          </div>
        )}
        {step === 2 && (
          <div>
            <label htmlFor="pz-discount" className="label-dark">Discount (% off the parking fare)</label>
            <input id="pz-discount" data-testid="pz-discount" type="number" min="1" max="100" step="1" value={f.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} className="input-dark !w-40" placeholder="15" />
            <p className="mt-2 text-[11px] text-zinc-600">Applied by the pricing engine server-side — never by the client.</p>
          </div>
        )}
        {step === 3 && (
          <div className="grid grid-cols-2 gap-4" data-testid="pz-validity">
            <div>
              <label htmlFor="pz-start" className="label-dark">Starts (UTC)</label>
              <input id="pz-start" data-testid="pz-start" type="datetime-local" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} className="input-dark" />
            </div>
            <div>
              <label htmlFor="pz-end" className="label-dark">Ends (UTC)</label>
              <input id="pz-end" data-testid="pz-end" type="datetime-local" value={f.ends_at} onChange={(e) => set("ends_at", e.target.value)} className="input-dark" />
            </div>
            <p className="col-span-2 text-[11px] text-zinc-600">Leave blank for open-ended. Validity is decided by canonical server time.</p>
          </div>
        )}
        {step === 4 && (
          <div className="grid grid-cols-2 gap-4" data-testid="pz-usage">
            <div>
              <label htmlFor="pz-max" className="label-dark">Max total redemptions</label>
              <input id="pz-max" data-testid="pz-max" type="number" min="1" value={f.max_redemptions} onChange={(e) => set("max_redemptions", e.target.value)} className="input-dark" placeholder="Unlimited" />
            </div>
            <div>
              <label htmlFor="pz-per-user" className="label-dark">Per-user limit</label>
              <input id="pz-per-user" data-testid="pz-per-user" type="number" min="1" value={f.per_user_limit} onChange={(e) => set("per_user_limit", e.target.value)} className="input-dark" placeholder="1" />
            </div>
            <p className="col-span-2 text-[11px] text-zinc-600">Enforced atomically server-side — duplicates, races and replays lose.</p>
          </div>
        )}
        {step === 5 && (
          <div className="rounded-xl border border-[#27272A] bg-[#0D1114]/60 p-5 text-sm space-y-2" data-testid="pz-review">
            <p className="meta-label mb-2">Review — exact server-authoritative configuration</p>
            {[["Code", f.code.trim().toUpperCase()], ["Audience", f.audience], ["Discount", `${f.discount_percent}% off the parking fare`],
              ["Starts (UTC)", f.starts_at || "Immediately on publish"], ["Ends (UTC)", f.ends_at || "Open-ended"],
              ["Max redemptions", f.max_redemptions || "Unlimited"], ["Per-user limit", f.per_user_limit || "1"]].map(([l, v]) => (
              <div key={l} className="flex justify-between"><span className="text-zinc-500">{l}</span><span className="font-semibold">{v}</span></div>
            ))}
          </div>
        )}
        {step === 6 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="pz-publish">
            <p className="text-sm font-semibold text-amber-400">Publish {f.code.trim().toUpperCase()}?</p>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">The promotion becomes redeemable immediately by the selected audience. You can deactivate it at any time — completed bookings are never affected.</p>
          </div>
        )}

        <div className="mt-6 flex justify-between">
          <button type="button" onClick={() => (step === 0 ? onClose() : setStep(step - 1))} data-testid="pz-back" className="btn-secondary !px-4 text-xs">
            <ChevronLeft className="w-3.5 h-3.5" /> {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < 6 ? (
            <button type="button" onClick={() => stepValid() ? setStep(step + 1) : toast.error("Complete this step first")} disabled={!stepValid()} data-testid="pz-next" className="btn-primary !px-4 text-xs disabled:opacity-40">
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button type="button" onClick={publish} disabled={busy} data-testid="pz-publish-btn" className="btn-primary !px-4 text-xs disabled:opacity-40">
              <Rocket className="w-3.5 h-3.5" /> {busy ? "Publishing…" : "Confirm & publish"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPromotions() {
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState("rewards"); // rewards | coupons
  const rewards = useAdminData(api.adminPromotions);
  const coupons = useAdminData(api.adminPromotions, { ptype: "coupon" });
  const [couponTab, setCouponTab] = useState("active");
  const [wizard, setWizard] = useState(null); // null | {} | prefill

  useEffect(() => { api.adminPromotionMetrics().then(setMetrics).catch(() => {}); }, []);

  const cards = metrics && [
    { label: "Rewards issued", value: metrics.rewards_issued, tid: "promo-metric-issued" },
    { label: "Rewards redeemed", value: metrics.rewards_redeemed, tid: "promo-metric-redeemed" },
    { label: "Referrals attributed", value: metrics.referrals_attributed, tid: "promo-metric-referrals" },
    { label: "Referral redemptions", value: metrics.referrals_redeemed, tid: "promo-metric-referral-redeemed" },
    { label: "Promotion cost", value: fmtMoney(metrics.promotion_cost_cents / 100), tid: "promo-metric-cost" },
    { label: "First-booking conversion", value: metrics.first_booking_conversion != null ? `${(metrics.first_booking_conversion * 100).toFixed(1)}%` : "—", tid: "promo-metric-conversion" },
    { label: "Fraud rejections", value: metrics.fraud_rejections, tid: "promo-metric-fraud" },
  ];

  const couponRows = useMemo(() => (coupons.data?.items || []).filter((p) => classify(p) === couponTab), [coupons.data, couponTab]);

  const couponAction = async (p, action) => {
    try {
      await api.adminUpdatePromotion(p.promotion_id, { action });
      toast.success(`${p.code} ${action === "activate" ? "activated" : "deactivated"}`);
      coupons.reload();
    } catch (e) { toast.error(e.message); }
  };

  const duplicate = (p) => setWizard({
    code: `${p.code}-COPY`, audience: p.audience, discount_percent: String(p.discount_percent),
    starts_at: "", ends_at: "", max_redemptions: p.max_redemptions ? String(p.max_redemptions) : "",
    per_user_limit: String(p.per_user_limit || 1),
  });

  return (
    <div data-testid="admin-promotions-page">
      <div className="flex gap-2 mb-6" role="tablist" aria-label="Promotion type">
        {[["rewards", "Rewards"], ["coupons", "Coupons"]].map(([v, l]) => (
          <button key={v} role="tab" aria-selected={tab === v} data-testid={`promo-tab-${v}`} onClick={() => setTab(v)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${tab === v ? "bg-[#E50914] border-[#E50914]" : "bg-[#1F2328] border-[#27272A] text-zinc-300"}`}>{l}</button>
        ))}
      </div>

      {tab === "rewards" && (
        <>
          {cards && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="promo-metrics">
              {cards.map((c) => (
                <div key={c.tid} className="bg-[#16191D] border border-[#27272A] rounded-xl p-3.5" data-testid={c.tid}>
                  <p className="text-lg font-bold font-display">{c.value}</p>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-600 mt-0.5">{c.label}</p>
                </div>
              ))}
            </div>
          )}
          <FilterSelect value={rewards.filters.status || ""} onChange={(v) => rewards.setFilters({ ...rewards.filters, status: v, page: 1 })}
            options={[["", "All statuses"], ["available", "Available"], ["locked", "Locked"], ["redeemed", "Redeemed"], ["expired", "Expired"]]}
            testId="promo-filter-status" />
          {rewards.error ? <p className="text-red-400 text-sm" data-testid="promo-error">{rewards.error}</p> : (
            <>
              <TableCard>
                <table className="w-full text-sm">
                  <thead><tr><TH>User</TH><TH>Type</TH><TH>Discount</TH><TH>Status</TH><TH>Booking</TH><TH>Created</TH><TH>Redeemed</TH></tr></thead>
                  <tbody>
                    {(rewards.data?.items || []).map((p) => (
                      <tr key={p.promotion_id} className="border-t border-[#1F2328]" data-testid={`promo-row-${p.promotion_id}`}>
                        <td className="p-4 text-zinc-300">{p.user_email || p.user_id}</td>
                        <td className="p-4 text-zinc-400">{p.type === "referral_reward" ? "Referral" : "Spin reward"}</td>
                        <td className="p-4 font-semibold text-[#FF3B45]">{p.discount_percent}%</td>
                        <td className="p-4"><StatusBadge value={p.status} /></td>
                        <td className="p-4 text-zinc-500 font-mono text-xs">{p.booking_id ? `#${p.booking_id.slice(-6)}` : "—"}</td>
                        <td className="p-4 text-zinc-500 text-xs">{fmtDateTime(p.created_at)}</td>
                        <td className="p-4 text-zinc-500 text-xs">{p.redeemed_at ? fmtDateTime(p.redeemed_at) : "—"}</td>
                      </tr>
                    ))}
                    {!rewards.data?.items?.length && (
                      <tr><td colSpan={7} className="p-10 text-center text-zinc-500" data-testid="promo-empty">No promotions yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </TableCard>
              <Pager page={rewards.page} setPage={rewards.setPage} total={rewards.data?.total || 0} pageSize={rewards.data?.page_size || 25} />
            </>
          )}
        </>
      )}

      {tab === "coupons" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {[["active", "Active"], ["scheduled", "Scheduled"], ["drafts", "Drafts"], ["expired", "Expired"]].map(([v, l]) => (
              <button key={v} data-testid={`coupon-tab-${v}`} onClick={() => setCouponTab(v)}
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${couponTab === v ? "bg-[#E50914] border-[#E50914]" : "bg-[#1F2328] border-[#27272A] text-zinc-300"}`}>{l}</button>
            ))}
            <button onClick={() => setWizard({})} data-testid="coupon-create-btn" className="btn-primary !py-2 !px-4 text-xs ml-auto">
              <Plus className="w-3.5 h-3.5" /> Create promotion
            </button>
          </div>
          {coupons.error ? <p className="text-red-400 text-sm">{coupons.error}</p> : !coupons.data ? <p className="text-sm text-zinc-500">Loading…</p> : (
            <>
              <TableCard>
                <table className="w-full text-sm" data-testid="coupon-table">
                  <thead><tr><TH>Code</TH><TH>Audience</TH><TH>Discount</TH><TH>Start</TH><TH>Expiration</TH><TH>Usage</TH><TH>Status</TH><TH>Actions</TH></tr></thead>
                  <tbody>
                    {couponRows.map((p) => (
                      <tr key={p.promotion_id} className="border-t border-[#1F2328]" data-testid={`coupon-row-${p.code}`}>
                        <td className="p-4 font-mono font-semibold text-[#FF3B45]">{p.code}</td>
                        <td className="p-4 text-zinc-400 capitalize">{p.audience}</td>
                        <td className="p-4 text-zinc-300">{p.discount_percent}%</td>
                        <td className="p-4 text-zinc-500 text-xs">{p.starts_at ? fmtDateTime(p.starts_at) : "—"}</td>
                        <td className="p-4 text-zinc-500 text-xs">{p.ends_at ? fmtDateTime(p.ends_at) : "—"}</td>
                        <td className="p-4 text-zinc-400 text-xs" data-testid={`coupon-usage-${p.code}`}>{p.redemptions_count || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""} · {p.per_user_limit || 1}/user</td>
                        <td className="p-4"><StatusBadge value={classify(p) === "scheduled" ? "scheduled" : p.status} /></td>
                        <td className="p-4">
                          <div className="flex gap-1.5">
                            {["draft", "disabled"].includes(p.status) && (
                              <button onClick={() => couponAction(p, "activate")} data-testid={`coupon-activate-${p.code}`}
                                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#27272A] text-emerald-400 hover:border-emerald-500 transition-colors">Activate</button>
                            )}
                            {p.status === "active" && (
                              <button onClick={() => couponAction(p, "deactivate")} data-testid={`coupon-deactivate-${p.code}`}
                                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#27272A] text-amber-400 hover:border-amber-500 transition-colors">Deactivate</button>
                            )}
                            <button onClick={() => duplicate(p)} data-testid={`coupon-duplicate-${p.code}`}
                                    className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#27272A] text-zinc-400 hover:text-white transition-colors"><Copy className="w-3 h-3" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!couponRows.length && (
                      <tr><td colSpan={8} className="p-10 text-center text-zinc-500" data-testid="coupon-empty">No {couponTab} promotions.</td></tr>
                    )}
                  </tbody>
                </table>
              </TableCard>
              <Pager page={coupons.page} setPage={coupons.setPage} total={coupons.data?.total || 0} pageSize={coupons.data?.page_size || 25} />
            </>
          )}
        </>
      )}

      {wizard !== null && (
        <CreateWizard prefill={wizard.code ? wizard : null} onClose={() => setWizard(null)}
                      onPublished={() => { setWizard(null); coupons.reload(); }} />
      )}
    </div>
  );
}
